import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROLES } from './AppShell';
import { presenceApi, dayLabel } from '../lib/presenceApi';
import type { DayPresence } from '../lib/presenceApi';

/**
 * THE "WHO IS OUT TODAY" STRIP on the command centre.
 *
 * The trustee's brief was "when I open the dashboard I expect the whole school", and the two things
 * a head of school actually acts on before nine in the morning are which teachers are out and which
 * children are away. Both were in the database and neither reached a screen.
 *
 * Deliberately compact: names and the uncovered sections, then a link into the full day view where a
 * date can be chosen. The dashboard answers "is anything wrong today"; the page answers "what
 * exactly, and on which date".
 *
 * ⚠ THIS RENDERS NOTHING RATHER THAN AN ERROR when the call fails. It hangs off the FIRST screen a
 * trustee sees, and a red box there because one supporting endpoint 403'd for a role I mapped wrong
 * would cost more than the strip is worth. It fails silent and the rest of the dashboard stands.
 */
export function PresenceStrip() {
  const { user } = useAuth();
  const [d, setD] = useState<DayPresence | null>(null);

  // Mirrors the backend `hr` policy; the catch below covers it drifting.
  const canSeeReasons = !!user && (ROLES.hr as readonly string[]).includes(user.role);

  useEffect(() => {
    let alive = true;
    presenceApi
      .day({ withReasons: canSeeReasons })
      .catch(() => (canSeeReasons ? presenceApi.day({}) : Promise.reject(new Error('no'))))
      .then((r) => alive && setD(r))
      .catch(() => alive && setD(null));
    return () => {
      alive = false;
    };
  }, [canSeeReasons]);

  if (!d) return null;

  const childrenAway = d.studentsOnLeave + d.studentsAbsent;
  const uncovered = d.staffAwayList.filter((s) => s.classesUncovered.length > 0);

  // Nothing to report is itself worth one line — an empty card reads as broken, a sentence does not.
  const quiet = d.staffAwayToday === 0 && childrenAway === 0;

  return (
    <div className="card presence-strip" style={{ padding: 16, marginBottom: 16 }}>
      <div className="presence-strip-head">
        <div>
          <div className="viz-title" style={{ marginBottom: 2 }}>Who is not here today</div>
          <p className="viz-sub" style={{ marginBottom: 0 }}>
            {d.isNonWorkingDay
              ? `${d.nonWorkingReason} — the register is not expected today.`
              : quiet
                ? 'Full attendance in the staffroom and nobody flagged on the roll.'
                : `${d.staffAwayToday} staff on leave · ${childrenAway} children away`}
          </p>
        </div>
        <Link to="/presence" className="btn btn-ghost" style={{ whiteSpace: 'nowrap' }}>
          Pick a date →
        </Link>
      </div>

      <div className="presence-strip-cols">
        {/* ---------------- staff ---------------- */}
        <div>
          <div className="presence-strip-label">Staff on leave</div>
          {d.staffAwayList.length === 0 ? (
            <p className="presence-strip-empty">Nobody on approved leave today.</p>
          ) : (
            <ul className="presence-strip-list">
              {d.staffAwayList.slice(0, 6).map((s) => (
                <li key={s.employeeId}>
                  <strong>{s.fullName}</strong>
                  <span className="presence-strip-meta">
                    {s.isHalfDay ? ' · half day' : s.daysRemaining > 0
                      ? ` · back in ${s.daysRemaining + 1} days` : ' · back tomorrow'}
                    {s.leaveType ? ` · ${s.leaveType}` : ''}
                  </span>
                </li>
              ))}
              {d.staffAwayList.length > 6 && (
                <li className="presence-strip-meta">
                  and {d.staffAwayList.length - 6} more
                </li>
              )}
            </ul>
          )}
          {uncovered.length > 0 && (
            <p className="presence-strip-warn">
              ⚠ No class teacher today for{' '}
              {uncovered.flatMap((s) => s.classesUncovered).join(', ')}.
            </p>
          )}
          {d.staffPendingCount > 0 && (
            <p className="presence-strip-meta" style={{ marginTop: 6 }}>
              {d.staffPendingCount} leave request{d.staffPendingCount === 1 ? '' : 's'} waiting for a decision.
            </p>
          )}
        </div>

        {/* ---------------- children ---------------- */}
        <div>
          <div className="presence-strip-label">Children away</div>
          {/* ⚠ registerTaken, not a zero count. On a day nobody has marked, "0 absent" is a lie. */}
          {!d.registerTaken ? (
            <p className="presence-strip-empty">
              The register has not been marked yet today.
              {d.studentsOnLeave > 0 && ` ${d.studentsOnLeave} children have approved leave.`}
            </p>
          ) : (
            <>
              <div className="presence-strip-nums">
                <span><b>{d.studentsOnLeave}</b> on leave</span>
                <span className={d.studentsAbsent > 0 ? 'is-bad' : ''}>
                  <b>{d.studentsAbsent}</b> absent
                </span>
                <span><b>{d.studentsLate}</b> late</span>
                {d.studentsUnmarked > 0 && (
                  <span className="is-warn"><b>{d.studentsUnmarked}</b> not marked</span>
                )}
              </div>
              {(() => {
                // The three classes with the most unexplained absence — the ones to ring first.
                const worst = [...d.classes]
                  .filter((c) => c.absent > 0)
                  .sort((a, b) => b.absent - a.absent)
                  .slice(0, 3);
                if (worst.length === 0) {
                  return <p className="presence-strip-empty">No unexplained absence anywhere today.</p>;
                }
                return (
                  <ul className="presence-strip-list">
                    {worst.map((c) => (
                      <li key={c.classKey}>
                        <strong>{c.classLabel}</strong>
                        <span className="presence-strip-meta">
                          {' '}· {c.absent} absent
                          {c.presentPct !== null ? ` · ${c.presentPct.toFixed(0)}% present` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </>
          )}
          <p className="presence-strip-meta" style={{ marginTop: 6 }}>
            {dayLabel(d.date)}
            {d.isNonWorkingDay && d.nonWorkingReason ? ` · ${d.nonWorkingReason}` : ''}
          </p>
        </div>
      </div>
    </div>
  );
}
